import { __spreadArrays } from "tslib";
import { fireNonCancelableEvent } from '../internal/events';
import { useUniqueId } from '../internal/hooks/use-unique-id';
import { findUpUntil } from '../internal/utils/dom';
import { getTrackableValue } from './utils';
import selectionStyles from './selection-control/styles.css.js';
var SELECTION_ITEM = 'selection-item';
var SELECTION_ROOT = 'selection-root';
function findSelectionControlByIndex(rootContainer, index) {
    if (index === -1) {
        // find "select all" checkbox
        return rootContainer.querySelector("[data-" + SELECTION_ITEM + "=\"all\"] ." + selectionStyles.root + " input");
    }
    return rootContainer.querySelectorAll("[data-" + SELECTION_ITEM + "=\"item\"] ." + selectionStyles.root + " input")[index];
}
function findRootContainer(element) {
    return findUpUntil(element, function (node) { return node.dataset.selectionRoot === 'true'; });
}
export function useFocusMove(selectionType, totalItems) {
    var _a, _b, _c;
    if (selectionType !== 'multi') {
        return {};
    }
    function moveFocus(sourceElement, fromIndex, direction) {
        var index = fromIndex;
        var rootContainer = findRootContainer(sourceElement);
        while (index >= -1 && index < totalItems) {
            index += direction;
            var control = findSelectionControlByIndex(rootContainer, index);
            if (control && !control.disabled) {
                control.focus();
                break;
            }
        }
    }
    var _d = [1, -1].map(function (direction) {
        return function (event) {
            var target = event.currentTarget;
            var itemNode = findUpUntil(target, function (node) { return node.dataset.selectionItem === 'item'; });
            var fromIndex = Array.prototype.indexOf.call(itemNode.parentElement.children, itemNode);
            moveFocus(target, fromIndex, direction);
        };
    }), moveFocusDown = _d[0], moveFocusUp = _d[1];
    return {
        moveFocusDown: moveFocusDown,
        moveFocusUp: moveFocusUp,
        moveFocus: moveFocus,
        focusMarkers: {
            item: (_a = {}, _a['data-' + SELECTION_ITEM] = 'item', _a),
            all: (_b = {}, _b['data-' + SELECTION_ITEM] = 'all', _b),
            root: (_c = {}, _c['data-' + SELECTION_ROOT] = 'true', _c)
        }
    };
}
export function useSelection(_a) {
    var items = _a.items, _b = _a.selectedItems, selectedItems = _b === void 0 ? [] : _b, selectionType = _a.selectionType, _c = _a.isItemDisabled, isItemDisabled = _c === void 0 ? function () { return false; } : _c, trackBy = _a.trackBy, onSelectionChange = _a.onSelectionChange, ariaLabels = _a.ariaLabels;
    var selectionName = useUniqueId();
    var finalSelectedItems = selectionType === 'single' ? selectedItems.slice(0, 1) : selectedItems;
    var selectedKeys = finalSelectedItems.map(function (item) { return getTrackableValue(trackBy, item); });
    var isItemSelected = function (item) { return selectedKeys.indexOf(getTrackableValue(trackBy, item)) > -1; };
    var getItemState = function (item) { return ({
        disabled: isItemDisabled(item),
        selected: isItemSelected(item)
    }); };
    var _d = selectionType
        ? items.reduce(function (_a, item) {
            var allDisabled = _a[0], allEnabledSelected = _a[1], hasSelected = _a[2];
            var _b = getItemState(item), disabled = _b.disabled, selected = _b.selected;
            return [
                // all items are disabled (or none are present)
                allDisabled && disabled,
                // all enabled items are selected (or none are present)
                allEnabledSelected && (selected || disabled),
                // the page has at least one selected item
                hasSelected || selected
            ];
        }, [true, true, false])
        : [true, true, false], allDisabled = _d[0], allEnabledSelected = _d[1], hasSelected = _d[2];
    var handleToggleAll = function () {
        var newSelectedItems = items.filter(function (item) {
            var _a = getItemState(item), disabled = _a.disabled, selected = _a.selected;
            if (allEnabledSelected) {
                // deselect all except disabled
                return selected && disabled;
            }
            else {
                // select all except disabled
                return selected || !disabled;
            }
        });
        fireNonCancelableEvent(onSelectionChange, { selectedItems: newSelectedItems });
    };
    var handleToggleItem = function (item) { return function () {
        var _a = getItemState(item), disabled = _a.disabled, selected = _a.selected;
        if (disabled || (selectionType === 'single' && selected)) {
            return;
        }
        if (selectionType === 'single') {
            fireNonCancelableEvent(onSelectionChange, { selectedItems: [item] });
        }
        else {
            var newSelectedItems = selected
                ? finalSelectedItems.filter(function (selectedItem) { return getTrackableValue(trackBy, item) !== getTrackableValue(trackBy, selectedItem); })
                : __spreadArrays(finalSelectedItems, [item]);
            fireNonCancelableEvent(onSelectionChange, { selectedItems: newSelectedItems });
        }
    }; };
    var getRowLabel = function (item) {
        return ariaLabels && ariaLabels.selectionGroupLabel + ' ' + ariaLabels.itemSelectionLabel({ selectedItems: selectedItems }, item);
    };
    return {
        isItemSelected: isItemSelected,
        selectAllProps: {
            name: selectionName,
            disabled: allDisabled,
            selectionType: selectionType,
            indeterminate: hasSelected && !allEnabledSelected,
            checked: hasSelected && allEnabledSelected,
            onChange: handleToggleAll,
            ariaLabel: ariaLabels &&
                ariaLabels.selectionGroupLabel +
                    ' ' +
                    (ariaLabels.allItemsSelectionLabel && ariaLabels.allItemsSelectionLabel({ selectedItems: selectedItems }))
        },
        getItemSelectionProps: function (item) { return ({
            name: selectionName,
            selectionType: selectionType,
            ariaLabel: getRowLabel(item),
            onChange: handleToggleItem(item),
            checked: isItemSelected(item),
            disabled: isItemDisabled(item)
        }); }
    };
}
